import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import SearchIcon from "@mui/icons-material/Search";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";

function StocManagerLayer() {
  const { groupId } = useParams();
  const navigate = useNavigate();

  // Data
  const [summary, setSummary] = useState({
    folders: 0,
    products: 0,
    items: 0,
    totalValue: 0,
  });
  const [folders, setFolders] = useState([]); // level 2 under current group
  const [products, setProducts] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [isLayer1, setIsLayer1] = useState(false);
  const [loading, setLoading] = useState(true);

  // UI state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editedName, setEditedName] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [productFormData, setProductFormData] = useState({
    product_name: "",
    sku: "",
    price: "",
    quantity: "",
    web_link: "",
    tags: "",
    notes: "",
    image_base64: "",
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");

  // Search within this layer (optional, local filter)
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const groupRes = await axios.get(
          `https://server-9p4hx.kinsta.app/api/stocmanager/folders/${groupId}`,
        );
        setGroupName(groupRes.data.name);
        const layer1 = !groupRes.data.parent_id;
        setIsLayer1(layer1);

        const [summaryRes, foldersRes, productsRes] = await Promise.all([
          axios.get(
            `https://server-9p4hx.kinsta.app/api/stocmanager/summary?${layer1 ? "parentId" : "folderId"}=${groupId}`,
          ),
          axios.get(
            `https://server-9p4hx.kinsta.app/api/stocmanager/folders/previews?parentId=${groupId}`,
          ),
          axios.get(
            `https://server-9p4hx.kinsta.app/api/stocmanager/folders/${groupId}/products`,
          ),
        ]);
        setSummary(summaryRes.data);
        // enrich each subfolder with totals (products, items, totalValue)
        const foldersWithTotals = await Promise.all(
          (foldersRes.data || []).map(async (f) => {
            try {
              const r = await axios.get(
                `https://server-9p4hx.kinsta.app/api/stocmanager/summary?folderId=${f.id}`,
              );
              return {
                ...f,
                totalProducts: r.data.products,
                itemsCount: r.data.items,
                totalValue: r.data.totalValue,
              };
            } catch (e) {
              return { ...f, totalProducts: 0, itemsCount: 0, totalValue: 0 };
            }
          }),
        );
        setFolders(foldersWithTotals);
        setProducts(productsRes.data);
      } catch (e) {
        console.error("Eroare încărcare layer:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [groupId]);

  const refreshLists = async () => {
    try {
      const [foldersRes, productsRes] = await Promise.all([
        axios.get(
          `https://server-9p4hx.kinsta.app/api/stocmanager/folders/previews?parentId=${groupId}`,
        ),
        axios.get(
          `https://server-9p4hx.kinsta.app/api/stocmanager/folders/${groupId}/products`,
        ),
      ]);
      const foldersWithTotals = await Promise.all(
        (foldersRes.data || []).map(async (f) => {
          try {
            const r = await axios.get(
              `https://server-9p4hx.kinsta.app/api/stocmanager/summary?folderId=${f.id}`,
            );
            return {
              ...f,
              totalProducts: r.data.products,
              itemsCount: r.data.items,
              totalValue: r.data.totalValue,
            };
          } catch (e) {
            return { ...f, totalProducts: 0, itemsCount: 0, totalValue: 0 };
          }
        }),
      );
      setFolders(foldersWithTotals);
      setProducts(productsRes.data);
    } catch (e) {
      console.error("Eroare refresh liste:", e);
    }
  };

  const updateFolderName = async (id) => {
    try {
      await axios.put(
        `https://server-9p4hx.kinsta.app/api/stocmanager/folders/${id}`,
        {
          name: editedName,
        },
      );
      setEditingId(null);
      await refreshLists();
    } catch (e) {
      console.error("Eroare redenumire:", e);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await axios.post(
        "https://server-9p4hx.kinsta.app/api/stocmanager/folders",
        {
          name: newFolderName,
          parent_id: Number(groupId),
        },
      );
      setCreateOpen(false);
      setNewFolderName("");
      await refreshLists();
      setSnackbarMsg("Folder creat");
      setSnackbarOpen(true);
    } catch (e) {
      console.error("Eroare creare folder:", e);
      setSnackbarMsg("Eroare la creare folder");
      setSnackbarOpen(true);
    }
  };

  const handleAddProduct = async () => {
    try {
      const folderId = selectedFolder?.id || Number(groupId);
      const check = await axios.get(
        `https://server-9p4hx.kinsta.app/api/stocmanager/folders/${folderId}`,
      );
      if (!check.data?.id) throw new Error("Folderul nu există");

      const formData = new FormData();
      Object.entries({
        product_name: productFormData.product_name,
        sku: productFormData.sku,
        price: productFormData.price,
        quantity: productFormData.quantity,
        web_link: productFormData.web_link,
        tags: productFormData.tags,
        notes: productFormData.notes,
        folder_id: folderId,
      }).forEach(([k, v]) => formData.append(k, v));

      if (productFormData.image_base64) {
        const byteString = atob(productFormData.image_base64.split(",")[1]);
        const mimeString = productFormData.image_base64
          .split(",")[0]
          .split(":")[1]
          .split(";")[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++)
          ia[i] = byteString.charCodeAt(i);
        const blob = new Blob([ab], { type: mimeString });
        formData.append("image", blob, "upload.jpg");
      }

      await axios.post(
        "https://server-9p4hx.kinsta.app/api/stocmanager/products/upload-image",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      setProductDialogOpen(false);
      setProductFormData({
        product_name: "",
        sku: "",
        price: "",
        quantity: "",
        web_link: "",
        tags: "",
        notes: "",
        image_base64: "",
      });
      await refreshLists();
    } catch (e) {
      console.error("Eroare adăugare produs:", e);
      setSnackbarMsg("Nu s-a putut adăuga produsul");
      setSnackbarOpen(true);
    }
  };

  const filteredProducts = products.filter(
    (p) =>
      (p.product_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.sku || "").toLowerCase().includes(searchTerm.toLowerCase()),
  );

  if (loading) {
    return (
      <Container sx={{ pt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ pt: 2, px: { xs: 1.5, md: 3 } }}>
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <IconButton aria-label="Înapoi" onClick={() => navigate(-1)}>
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
          <Typography variant="h5" fontWeight="bold">
            {groupName}
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            Subfolder
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setProductDialogOpen(true)}
          >
            Produs
          </Button>
        </Box>
      </Box>

      {/* Summary */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item md={3} sm={6} xs={12}>
            <Typography variant="body2" color="text.secondary">
              Items
            </Typography>
            <Typography variant="h6">{summary.items || 0}</Typography>
          </Grid>
          <Grid item md={3} sm={6} xs={12}>
            <Typography variant="body2" color="text.secondary">
              Produse
            </Typography>
            <Typography variant="h6">{summary.products}</Typography>
          </Grid>
          <Grid item md={3} sm={6} xs={12}>
            <Typography variant="body2" color="text.secondary">
              Folders
            </Typography>
            <Typography variant="h6">{summary.folders}</Typography>
          </Grid>
          <Grid item md={3} sm={6} xs={12}>
            <Typography variant="body2" color="text.secondary">
              Valoare
            </Typography>
            <Typography variant="h6">
              {Math.round(summary.totalValue).toLocaleString()} RON
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Search bar for products */}
      <TextField
        fullWidth
        placeholder="Caută produse după denumire sau SKU"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      <Grid container spacing={2}>
        {/* Folders list (Level 2) — 2 carduri pe un rând */}
        <Grid item md={6} xs={12}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Subfoldere
          </Typography>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
            }}
          >
            {folders.map((folder) => (
              <Paper
                key={folder.id}
                sx={{ p: 1.5, cursor: "pointer", width: "100%" }}
                onClick={() => navigate(`/folder/${folder.id}`)}
              >
                <Box display="flex" gap={1.5} alignItems="center">
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, 1fr)",
                      gap: 0.5,
                      minWidth: 64,
                    }}
                  >
                    {Array.from({ length: 4 }).map((_, idx) =>
                      folder.previewImages?.[idx] ? (
                        <Box
                          key={idx}
                          component="img"
                          src={folder.previewImages[idx]}
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1,
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <Box
                          key={idx}
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1,
                            bgcolor: "#eee",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <PhotoCameraIcon
                            sx={{ fontSize: 16, color: "#999" }}
                          />
                        </Box>
                      ),
                    )}
                  </Box>
                  <Box flex={1} minWidth={0}>
                    {editingId === folder.id ? (
                      <TextField
                        fullWidth
                        size="small"
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        onBlur={() => updateFolderName(folder.id)}
                        autoFocus
                      />
                    ) : (
                      <>
                        <Typography fontWeight={600} noWrap>
                          {folder.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Produse: {folder.totalProducts || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Bucăți: {folder.itemsCount || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Valoare:{" "}
                          {Number(folder.totalValue || 0).toLocaleString()} RON
                        </Typography>
                      </>
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAnchorEl(e.currentTarget);
                      setSelectedFolder(folder);
                      setSelectedProduct(null);
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Box>
              </Paper>
            ))}
          </Box>
        </Grid>

        {/* Products list */}
        <Grid item md={6} xs={12}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Produse
          </Typography>
          <Grid container spacing={2}>
            {filteredProducts.map((product) => (
              <Grid item key={product.id} md={4} sm={6} xs={12}>
                <Paper
                  sx={{ p: 1.5, cursor: "pointer" }}
                  onClick={() => navigate(`/product/${product.id}`)}
                >
                  <Box display="flex" gap={1.5}>
                    {product.image_url ? (
                      <Box
                        component="img"
                        src={product.image_url}
                        alt={product.product_name}
                        sx={{
                          width: 140,
                          height: 100,
                          borderRadius: 1,
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 140,
                          height: 100,
                          borderRadius: 1,
                          bgcolor: "#eee",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <PhotoCameraIcon sx={{ color: "#999" }} />
                      </Box>
                    )}
                    <Box flex={1} minWidth={0}>
                      <Typography
                        fontWeight={600}
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {product.product_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Preț: {Number(product.price).toLocaleString()} RON —
                        SKU: {product.sku}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAnchorEl(e.currentTarget);
                        setSelectedProduct(product);
                        setSelectedFolder(null);
                      }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>

      {/* Context menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {selectedFolder && (
          <>
            <MenuItem
              onClick={() => {
                setEditingId(selectedFolder.id);
                setEditedName(selectedFolder.name);
                setAnchorEl(null);
              }}
            >
              <EditIcon fontSize="small" sx={{ mr: 1 }} /> Editare folder
            </MenuItem>
            <MenuItem
              onClick={async () => {
                setAnchorEl(null);
                await axios.delete(
                  `https://server-9p4hx.kinsta.app/api/stocmanager/folders/${selectedFolder.id}`,
                );
                await refreshLists();
              }}
              sx={{ color: "error.main" }}
            >
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Șterge folder
            </MenuItem>
          </>
        )}
        {selectedProduct && (
          <>
            <MenuItem
              onClick={() => {
                navigate(`/product/${selectedProduct.id}`);
                setAnchorEl(null);
              }}
            >
              <EditIcon fontSize="small" sx={{ mr: 1 }} /> Detalii
            </MenuItem>
            <MenuItem
              onClick={async () => {
                setAnchorEl(null);
                await axios.delete(
                  `https://server-9p4hx.kinsta.app/api/stocmanager/products/${selectedProduct.id}`,
                );
                await refreshLists();
              }}
              sx={{ color: "error.main" }}
            >
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Șterge produs
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Create subfolder */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth>
        <DialogTitle>Adaugă subfolder</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nume subfolder"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Anulează</Button>
          <Button variant="contained" onClick={handleCreateFolder}>
            Salvează
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add product */}
      <Dialog
        open={productDialogOpen}
        onClose={() => setProductDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Adaugă produs</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              width: "100%",
              height: 180,
              bgcolor: "#fafafa",
              border: "1px dashed #ccc",
              borderRadius: 2,
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <input
              type="file"
              hidden
              id="dm-add-image"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onloadend = () =>
                  setProductFormData((prev) => ({
                    ...prev,
                    image_base64: reader.result,
                  }));
                reader.readAsDataURL(file);
              }}
            />
            <label htmlFor="dm-add-image">
              <Box sx={{ cursor: "pointer", textAlign: "center", px: 2 }}>
                <PhotoCameraIcon sx={{ fontSize: 32, color: "#999" }} />
                <Typography variant="caption" display="block">
                  Încarcă imagine
                </Typography>
              </Box>
            </label>
            {productFormData.image_base64 && (
              <Box
                component="img"
                src={productFormData.image_base64}
                alt="preview"
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: 2,
                }}
              />
            )}
          </Box>

          <TextField
            fullWidth
            label="Nume produs"
            value={productFormData.product_name}
            onChange={(e) =>
              setProductFormData({
                ...productFormData,
                product_name: e.target.value,
              })
            }
            sx={{ mb: 1 }}
          />
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Cantitate"
                type="number"
                value={productFormData.quantity}
                onChange={(e) =>
                  setProductFormData({
                    ...productFormData,
                    quantity: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Preț (RON)"
                type="number"
                value={productFormData.price}
                onChange={(e) =>
                  setProductFormData({
                    ...productFormData,
                    price: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="SKU"
                value={productFormData.sku}
                onChange={(e) =>
                  setProductFormData({
                    ...productFormData,
                    sku: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Tags"
                value={productFormData.tags}
                onChange={(e) =>
                  setProductFormData({
                    ...productFormData,
                    tags: e.target.value,
                  })
                }
              />
            </Grid>
          </Grid>
          <TextField
            fullWidth
            label="Notițe"
            multiline
            value={productFormData.notes}
            onChange={(e) =>
              setProductFormData({ ...productFormData, notes: e.target.value })
            }
            sx={{ mt: 1 }}
          />
          <TextField
            fullWidth
            label="Web Link"
            value={productFormData.web_link}
            onChange={(e) =>
              setProductFormData({
                ...productFormData,
                web_link: e.target.value,
              })
            }
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setProductDialogOpen(false)}
            startIcon={<CloseIcon />}
          >
            Anulează
          </Button>
          <Button
            onClick={handleAddProduct}
            variant="contained"
            startIcon={<SaveIcon />}
          >
            Salvează
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default StocManagerLayer;
